/**
 * AccountsPage
 *
 * Home page showing all accounts with balances.
 * Matches the design mockup with:
 * - Hero section (total balance + monthly change)
 * - Summary bar (income/expenses)
 * - Account list with colored icons
 * - Account actions (delete, deactivate)
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAccounts } from '@hooks/useAccounts';
import { useSummary } from '@hooks/useSummary';
import { usePrivacyMode } from '@hooks/usePrivacyMode';
import { useHoldings } from '@hooks/useHoldings';
import { accountService } from '@services/accounts';
import { transactionService } from '@services/transactions';
import { Account } from '@services/db';
import { Modal, Button, Select, Input, PrivacyToggle } from '@/components/shared';
import { AddAccountModal } from '@/components/AddAccountModal';
import { formatCurrency as formatCurrencyIntl } from '@/utils/format';
import { useLanguage } from '@/i18n';
import './AccountsPage.css';

// Account type colors
const ACCOUNT_COLORS: Record<string, { bg: string; color: string }> = {
  checking: { bg: '#0066ff20', color: '#0066ff' },
  savings: { bg: '#00d68f20', color: '#00d68f' },
  cash: { bg: '#00d63220', color: '#00d632' },
  credit: { bg: '#ff6b6b20', color: '#ff6b6b' },
  crypto: { bg: '#9d7aff20', color: '#9d7aff' },
  investment: { bg: '#f59e0b20', color: '#f59e0b' },
};

/**
 * Format currency for display.
 */
function formatCurrency(amount: number, currency = 'USD'): string {
  // Central locale-aware formatter (utils/format.ts) - never inline Intl here
  return formatCurrencyIntl(amount, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format currency with split for large display.
 * NOTE: splits at '.' - correct for en/ja/th (all use '.' decimals); revisit
 * if a comma-decimal locale (de, fr...) is ever added.
 */
function formatLargeCurrency(amount: number): { main: string; cents: string } {
  const formatted = formatCurrencyIntl(amount, 'USD', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Split at decimal point
  const parts = formatted.split('.');
  return {
    main: parts[0],
    cents: parts[1] ? `.${parts[1]}` : '.00',
  };
}

/**
 * Get account icon letter(s).
 */
function getAccountIcon(name: string, _fullName?: string): string {
  // Use first letter of short name, or first two if available
  if (name.length <= 2) return name.toUpperCase();
  return name.charAt(0).toUpperCase();
}

/**
 * Get account color based on type.
 */
function getAccountColor(type: string): { bg: string; color: string } {
  return ACCOUNT_COLORS[type] || { bg: '#55556620', color: '#888888' };
}

// Skeleton for loading state
function HeroSkeleton() {
  const { t } = useLanguage();
  return (
    <div className="hero">
      <div className="hero-label">{t('accounts.total_balance')}</div>
      <div className="skeleton hero-amount-skeleton" />
      <div className="skeleton hero-change-skeleton" />
    </div>
  );
}

function SummaryBarSkeleton() {
  return (
    <div className="summary-bar">
      <div className="stat-card">
        <div className="skeleton stat-skeleton" />
      </div>
      <div className="stat-card">
        <div className="skeleton stat-skeleton" />
      </div>
    </div>
  );
}

function AccountRowSkeleton() {
  return (
    <div className="account-row">
      <div className="skeleton account-icon-skeleton" />
      <div className="account-info">
        <div className="skeleton account-name-skeleton" />
        <div className="skeleton account-fullname-skeleton" />
      </div>
      <div className="skeleton account-balance-skeleton" />
    </div>
  );
}

// Account Actions Modal
interface AccountActionsModalProps {
  account: Account | null;
  allAccounts: Account[];
  onClose: () => void;
  // Resolves with the service result so a blocked delete (account still
  // referenced by transactions/schedules) can be explained inline.
  onDelete: (id: string) => Promise<{ success: boolean; transactionCount?: number; scheduleCount?: number }>;
  onDeactivate: (id: string) => Promise<void>;
  onTransfer: (fromId: string, toId: string, amount: number) => Promise<void>;
  onRename: (id: string, name: string, fullName?: string) => Promise<void>;
  onAdjustBalance: (id: string, newBalance: number, description: string) => Promise<void>;
  onRefresh: () => void;
}

function AccountActionsModal({
  account,
  allAccounts,
  onClose,
  onDelete,
  onDeactivate,
  onTransfer,
  onRename,
  onAdjustBalance,
  onRefresh,
}: AccountActionsModalProps) {
  const { t } = useLanguage();
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Set when a delete was refused because records still reference the
  // account - rendered as an inline notice (never a browser alert()).
  const [deleteBlocked, setDeleteBlocked] = useState<{ txns?: number; sched?: number } | null>(null);
  const [showHideConfirm, setShowHideConfirm] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTo, setTransferTo] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  // Rename state
  const [showRename, setShowRename] = useState(false);
  const [editName, setEditName] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  // Adjust Balance state
  const [showAdjustBalance, setShowAdjustBalance] = useState(false);
  const [newBalance, setNewBalance] = useState('');
  const [adjustDescription, setAdjustDescription] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);

  // BUG-046 FIX: Reset all submenu states when account changes (including null->account)
  // This prevents the modal from staying stuck in a submenu when reopened
  useEffect(() => {
    setShowDeleteConfirm(false);
    setDeleteBlocked(null);
    setShowHideConfirm(false);
    setShowTransfer(false);
    setTransferTo('');
    setTransferAmount('');
    setTransferError(null);
    setShowRename(false);
    setEditName('');
    setEditFullName('');
    setRenameError(null);
    setShowAdjustBalance(false);
    setNewBalance('');
    setAdjustDescription('');
    setAdjustError(null);
  }, [account?.id]);

  if (!account) return null;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const result = await onDelete(account.id!);
      if (result.success) {
        onClose();
      } else {
        // Deletion was prevented (account still referenced) - explain inline
        // and offer Hide as the safe alternative.
        setShowDeleteConfirm(false);
        setDeleteBlocked({ txns: result.transactionCount, sched: result.scheduleCount });
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDeactivate = async () => {
    await onDeactivate(account.id!);
    setShowHideConfirm(false);
    onClose();
  };

  // Adjust Balance handlers
  const handleStartAdjust = () => {
    setNewBalance(account.balance.toFixed(2));
    setAdjustDescription('');
    setAdjustError(null);
    setShowAdjustBalance(true);
  };

  const handleCancelAdjust = () => {
    setShowAdjustBalance(false);
    setNewBalance('');
    setAdjustDescription('');
    setAdjustError(null);
  };

  const handleSaveAdjust = async () => {
    const newBal = parseFloat(newBalance);
    if (isNaN(newBal)) {
      setAdjustError(t('accounts.actions.err_invalid_amount'));
      return;
    }

    const diff = newBal - account.balance;
    if (diff === 0) {
      setAdjustError(t('accounts.actions.err_unchanged'));
      return;
    }

    if (!adjustDescription.trim()) {
      setAdjustError(t('accounts.actions.err_need_desc'));
      return;
    }

    setAdjusting(true);
    setAdjustError(null);
    try {
      await onAdjustBalance(account.id!, newBal, adjustDescription.trim());
      setShowAdjustBalance(false);
      onRefresh();
      onClose();
    } catch (err) {
      setAdjustError(err instanceof Error ? err.message : t('accounts.actions.err_adjust_failed'));
    } finally {
      setAdjusting(false);
    }
  };

  // Calculate adjustment preview
  const getAdjustPreview = () => {
    const newBal = parseFloat(newBalance);
    if (isNaN(newBal)) return null;
    const diff = newBal - account.balance;
    if (diff === 0) return null;
    return {
      diff,
      type: diff > 0 ? 'income' : 'expense',
      label: diff > 0 ? t('common.income') : t('common.expense'),
      amount: Math.abs(diff),
    };
  };

  const adjustPreview = getAdjustPreview();

  const handleTransfer = async () => {
    const amount = parseFloat(transferAmount);
    if (!transferTo) {
      setTransferError(t('accounts.actions.err_select_dest'));
      return;
    }
    if (isNaN(amount) || amount <= 0) {
      setTransferError(t('accounts.actions.err_invalid_amount'));
      return;
    }

    setTransferring(true);
    setTransferError(null);
    try {
      await onTransfer(account.id!, transferTo, amount);
      setShowTransfer(false);
      setTransferTo('');
      setTransferAmount('');
      onRefresh();
      onClose();
    } catch (err) {
      setTransferError(err instanceof Error ? err.message : t('accounts.actions.err_transfer_failed'));
    } finally {
      setTransferring(false);
    }
  };

  const handleStartRename = () => {
    setEditName(account.name);
    setEditFullName(account.fullName || '');
    setRenameError(null);
    setShowRename(true);
  };

  const handleCancelRename = () => {
    setShowRename(false);
    setEditName('');
    setEditFullName('');
    setRenameError(null);
  };

  const handleSaveRename = async () => {
    const trimmedName = editName.trim();
    if (!trimmedName) {
      setRenameError(t('accounts.actions.err_name_required'));
      return;
    }

    setRenaming(true);
    setRenameError(null);
    try {
      await onRename(account.id!, trimmedName, editFullName.trim() || undefined);
      setShowRename(false);
      onRefresh();
      onClose();
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : t('accounts.actions.err_rename_failed'));
    } finally {
      setRenaming(false);
    }
  };

  // Other accounts for transfer (excluding current)
  const otherAccounts = allAccounts.filter(a => a.id !== account.id);

  return (
    <Modal isOpen={!!account} onClose={onClose} title={`${account.fullName || account.name}`}>
      <div className="account-actions-modal">
        {/* Account Info */}
        <div className="account-actions-info">
          <div className="info-row">
            <span className="info-label">{t('accounts.actions.balance')}</span>
            <span className="info-value">{formatCurrency(account.balance, account.currency)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">{t('accounts.actions.type')}</span>
            <span className="info-value">{t(`accounts.type.${account.type}`)}</span>
          </div>
        </div>

        {/* Rename Section */}
        {showRename ? (
          <div className="rename-form">
            <h4 className="rename-title">{t('accounts.actions.rename_account')}</h4>

            <Input
              label={t('accounts.actions.short_name')}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={t('accounts.actions.short_name_placeholder')}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') handleCancelRename();
              }}
            />

            <Input
              label={t('accounts.actions.full_name')}
              value={editFullName}
              onChange={(e) => setEditFullName(e.target.value)}
              placeholder={t('accounts.actions.full_name_placeholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') handleCancelRename();
              }}
            />

            {renameError && (
              <div className="rename-error">{renameError}</div>
            )}

            <div className="rename-buttons">
              <Button variant="ghost" onClick={handleCancelRename}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveRename} disabled={renaming}>
                {renaming ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        ) : showAdjustBalance ? (
          <div className="adjust-balance-form">
            <h4 className="form-title">{t('accounts.actions.adjust_balance')}</h4>

            <div className="current-balance-display">
              <span className="label">{t('accounts.actions.current_balance')}</span>
              <span className="value">{formatCurrency(account.balance, account.currency)}</span>
            </div>

            <Input
              type="number"
              label={t('accounts.actions.new_balance')}
              value={newBalance}
              onChange={(e) => setNewBalance(e.target.value)}
              placeholder="0.00"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveAdjust();
                if (e.key === 'Escape') handleCancelAdjust();
              }}
            />

            {adjustPreview && (
              <div className={`adjust-preview ${adjustPreview.type}`}>
                <span className="preview-label">{t('accounts.actions.will_create')}</span>
                <span className={`preview-value amount--${adjustPreview.type}`}>
                  {adjustPreview.type === 'income' ? '+' : '-'}
                  {formatCurrency(adjustPreview.amount, account.currency)} {adjustPreview.label}
                </span>
              </div>
            )}

            <Input
              label={t('accounts.actions.desc_required')}
              value={adjustDescription}
              onChange={(e) => setAdjustDescription(e.target.value)}
              placeholder={t('accounts.actions.desc_placeholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveAdjust();
                if (e.key === 'Escape') handleCancelAdjust();
              }}
            />

            {adjustError && (
              <div className="form-error">{adjustError}</div>
            )}

            <div className="form-buttons">
              <Button variant="ghost" onClick={handleCancelAdjust}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveAdjust} disabled={adjusting}>
                {adjusting ? t('common.saving') : t('accounts.actions.save_adjustment')}
              </Button>
            </div>
          </div>
        ) : showTransfer ? (
          <div className="transfer-form">
            <h4 className="form-title">{t('accounts.actions.transfer_money')}</h4>

            <Select
              label={t('accounts.actions.to_account')}
              value={transferTo}
              onChange={(e) => setTransferTo(e.target.value)}
              options={[
                { value: '', label: t('accounts.actions.select_destination') },
                ...otherAccounts.map(a => ({
                  value: a.id!,
                  label: a.fullName ? `${a.name} (${a.fullName})` : a.name,
                })),
              ]}
            />

            <div className="transfer-amount-row">
              <Input
                type="number"
                label={t('accounts.actions.amount')}
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            {transferError && (
              <div className="form-error">{transferError}</div>
            )}

            <div className="form-buttons">
              <Button variant="ghost" onClick={() => setShowTransfer(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleTransfer} disabled={transferring}>
                {transferring ? t('accounts.actions.transferring') : t('accounts.actions.transfer')}
              </Button>
            </div>
          </div>
        ) : showHideConfirm ? (
          <div className="confirm-dialog">
            <div className="confirm-icon">👁️</div>
            <h4 className="confirm-title">{t('accounts.actions.hide_title')}</h4>
            <p className="confirm-message">
              {t('accounts.actions.hide_message', { name: account.fullName || account.name })}
            </p>
            <div className="confirm-buttons">
              <Button variant="ghost" onClick={() => setShowHideConfirm(false)}>
                {t('accounts.actions.no_cancel')}
              </Button>
              <Button variant="secondary" onClick={handleDeactivate}>
                {t('accounts.actions.yes_hide')}
              </Button>
            </div>
          </div>
        ) : deleteBlocked ? (
          <div className="confirm-dialog">
            <div className="confirm-icon">🔒</div>
            <h4 className="confirm-title">{t('accounts.actions.delete_blocked_title')}</h4>
            <p className="confirm-message">
              {deleteBlocked.sched
                ? t('accounts.actions.delete_blocked_sched', { count: deleteBlocked.sched })
                : t('accounts.actions.delete_blocked_txns', { count: deleteBlocked.txns ?? 0 })}
            </p>
            <div className="confirm-buttons">
              <Button variant="ghost" onClick={() => setDeleteBlocked(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setDeleteBlocked(null);
                  setShowHideConfirm(true);
                }}
              >
                {t('accounts.actions.hide_instead')}
              </Button>
            </div>
          </div>
        ) : showDeleteConfirm ? (
          <div className="confirm-dialog">
            <div className="confirm-icon danger">⚠️</div>
            <h4 className="confirm-title">{t('accounts.actions.delete_title')}</h4>
            <p className="confirm-message">
              {t('accounts.actions.delete_message', { name: account.fullName || account.name })}
            </p>
            <div className="confirm-buttons">
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
                {t('accounts.actions.no_cancel')}
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? t('accounts.actions.deleting') : t('accounts.actions.yes_delete')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="account-actions-buttons">
            <Button variant="primary" onClick={handleStartAdjust}>
              {t('accounts.actions.adjust_balance')}
            </Button>

            <Button variant="secondary" onClick={handleStartRename}>
              {t('accounts.actions.rename_account')}
            </Button>

            <Button variant="secondary" onClick={() => setShowTransfer(true)}>
              {t('accounts.actions.transfer_money')}
            </Button>

            {/* Hide/Delete both confirm before acting - keep them visually
                separated from the everyday actions above */}
            <div className="actions-divider" role="separator" />

            <Button variant="secondary" onClick={() => setShowHideConfirm(true)}>
              {t('accounts.actions.hide_account')}
            </Button>

            <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
              {t('accounts.actions.delete_account')}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function AccountsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { accounts, loading: accountsLoading, error: accountsError, refresh } = useAccounts();
  const { data: summary, loading: summaryLoading } = useSummary();
  const { isPrivacyMode } = usePrivacyMode();
  // Auto-detected external holdings (crypto) - appears only when the
  // capital-move detector finds activity or adjustments exist. Derived +
  // local-only: NOT part of totalBalance (bank total stays "spendable").
  const { holdings } = useHoldings();
  const visibleHoldings = holdings.filter(
    (h) => h.matchCount > 0 || h.adjustments.length > 0
  );

  // Zero-balance accounts collapse below the main list (count always
  // visible; preference persisted). Skipped when it would leave the main
  // list empty. Same minimal pattern as the metrics_dev inactive users.
  const [showZeroBalance, setShowZeroBalance] = useState(() => {
    try {
      return localStorage.getItem('ninjacash_show_zero_accounts') === '1';
    } catch {
      return false;
    }
  });
  const toggleZeroBalance = useCallback(() => {
    setShowZeroBalance((v) => {
      try {
        localStorage.setItem('ninjacash_show_zero_accounts', v ? '0' : '1');
      } catch { /* preference just won't persist */ }
      return !v;
    });
  }, []);

  const zeroAccounts = useMemo(() => accounts.filter((a) => a.balance === 0), [accounts]);
  const collapseZero = zeroAccounts.length > 0 && zeroAccounts.length < accounts.length;
  const displayedAccounts = useMemo(() => {
    if (!collapseZero) return accounts;
    const nonZero = accounts.filter((a) => a.balance !== 0);
    return showZeroBalance ? [...nonZero, ...zeroAccounts] : nonZero;
  }, [accounts, zeroAccounts, collapseZero, showZeroBalance]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [showAddAccount, setShowAddAccount] = useState(false);

  // Hidden accounts - unhide directly from this page (collapsed section at
  // the bottom; previously only reachable via Settings). Reloads whenever
  // the active list changes (hide/unhide/sync all emit 'accounts').
  const [hiddenAccounts, setHiddenAccounts] = useState<Account[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const [unhidingId, setUnhidingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    accountService
      .getHidden()
      .then((hidden) => {
        if (!cancelled) {
          setHiddenAccounts(hidden);
          // Auto-collapse when the last hidden account is restored
          if (hidden.length === 0) setShowHidden(false);
        }
      })
      .catch((err) => {
        console.error('[AccountsPage] Failed to load hidden accounts:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [accounts]);

  const handleUnhideAccount = useCallback(async (id: string) => {
    setUnhidingId(id);
    try {
      await accountService.activate(id);
      refresh();
    } catch (err) {
      console.error('[AccountsPage] Failed to unhide account:', err);
    } finally {
      setUnhidingId(null);
    }
  }, [refresh]);

  // Drag and drop state
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const dragCounter = useRef(0);

  // Directional drop indicator: a row above the dragged row shows the line
  // on top (item lands before it), a row below shows it underneath (item
  // lands after it) - matches where handleDrop actually inserts.
  const draggedIdx = draggedId
    ? displayedAccounts.findIndex((a) => a.id === draggedId)
    : -1;

  const loading = accountsLoading || summaryLoading;

  // Handle retry on error
  const handleRetry = () => {
    refresh();
  };

  // Handle account click - open actions modal
  const handleAccountClick = useCallback((account: Account) => {
    console.log('[AccountsPage] Account clicked:', account.name, account.id);
    setSelectedAccount(account);
  }, []);

  // Handle delete account
  // The service refuses when transactions/schedules still reference the
  // account; the modal renders that result inline (no alert()).
  const handleDeleteAccount = useCallback(async (id: string) => {
    const result = await accountService.delete(id);
    if (result.success) {
      refresh();
    }
    return result;
  }, [refresh]);

  // Handle deactivate account (hide)
  const handleDeactivateAccount = useCallback(async (id: string) => {
    await accountService.deactivate(id);
    refresh();
  }, [refresh]);

  // Handle transfer between accounts
  // NOTE: DO NOT call recalculateAllBalances() - it breaks balances due to old corrupted CSV data
  // Balances are managed manually via DevPage
  const handleTransfer = useCallback(async (fromId: string, toId: string, amount: number) => {
    const fromAccount = accounts.find(a => a.id === fromId);
    const toAccount = accounts.find(a => a.id === toId);
    if (!fromAccount || !toAccount) throw new Error('Account not found');

    // Create transfer transaction
    await transactionService.create({
      type: 'transfer',
      amount,
      accountId: fromId,
      toAccountId: toId,
      description: `Transfer to ${toAccount.fullName || toAccount.name}`,
      date: new Date(),
      currency: fromAccount.currency || 'USD',
    });

    // Manually update balances for the two affected accounts only
    // Subtract from source, add to destination
    await accountService.update(fromId, { balance: fromAccount.balance - amount });
    await accountService.update(toId, { balance: toAccount.balance + amount });

    console.log('[AccountsPage] Transfer created:', amount, 'from', fromAccount.name, 'to', toAccount.name);
  }, [accounts]);

  // Handle rename account
  const handleRenameAccount = useCallback(async (id: string, name: string, fullName?: string) => {
    await accountService.update(id, { name, fullName });
    console.log('[AccountsPage] Account renamed:', id, 'to', name, fullName ? `(${fullName})` : '');
  }, []);

  // Handle balance adjustment
  // Creates an income or expense transaction based on the difference
  const handleAdjustBalance = useCallback(async (id: string, newBalance: number, description: string) => {
    const account = accounts.find(a => a.id === id);
    if (!account) throw new Error('Account not found');

    const diff = newBalance - account.balance;
    if (diff === 0) return;

    // Create adjustment transaction
    const type = diff > 0 ? 'income' : 'expense';
    await transactionService.create({
      type,
      amount: Math.abs(diff),
      accountId: id,
      description: `[Adjustment] ${description}`,
      date: new Date(),
      currency: account.currency || 'USD',
    });

    // Note: transactionService.create() already updates the balance via adjustAccountBalances()
    // So we don't need to manually update the account balance here

    console.log('[AccountsPage] Balance adjusted:', account.name, account.balance, '->', newBalance, `(${type} ${Math.abs(diff)})`);
  }, [accounts]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    // Add a slight delay before applying drag styles
    setTimeout(() => {
      const el = e.target as HTMLElement;
      el.classList.add('dragging');
    }, 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    el.classList.remove('dragging');
    setDraggedId(null);
    setDragOverId(null);
    dragCounter.current = 0;
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>, id: string) => {
    e.preventDefault();
    dragCounter.current++;
    if (id !== draggedId) {
      setDragOverId(id);
    }
  }, [draggedId]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverId(null);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // Shared by HTML5 drop (desktop) and pointer-based touch drag (mobile)
  const commitReorder = useCallback(async (sourceId: string, targetId: string) => {
    if (!sourceId || sourceId === targetId) return;

    const sourceIndex = accounts.findIndex(a => a.id === sourceId);
    const targetIndex = accounts.findIndex(a => a.id === targetId);
    if (sourceIndex === -1 || targetIndex === -1) return;

    const newOrder = [...accounts];
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, removed);

    const orderedIds = newOrder.map(a => a.id!);
    await accountService.updateOrder(orderedIds);

    console.log('[AccountsPage] Reordered accounts:', orderedIds);
    refresh();
  }, [accounts, refresh]);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');
    setDragOverId(null);

    // Ignore drops that did not originate from an account row (e.g. a file
    // or text dragged in from outside the app)
    if (!sourceId) return;
    await commitReorder(sourceId, targetId);
  }, [commitReorder]);

  // --- Touch reorder (mobile) ---
  // HTML5 drag-and-drop never fires from touch input, so the drag handle
  // doubles as a pointer-drag grip: touch it and slide over another row to
  // reorder. Mouse input is excluded (it keeps the HTML5 path), and
  // touch-action: none on the handle stops the page from scrolling instead.
  const touchDrag = useRef<{ pointerId: number; sourceId: string } | null>(null);

  const rowIdFromPoint = (x: number, y: number): string | null => {
    const el = document.elementFromPoint(x, y);
    const row = el?.closest('[data-account-id]') as HTMLElement | null;
    return row?.dataset.accountId ?? null;
  };

  const handleTouchDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>, id: string) => {
    if (e.pointerType === 'mouse') return;
    touchDrag.current = { pointerId: e.pointerId, sourceId: id };
    setDraggedId(id);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch { /* synthetic events may lack a real pointer id */ }
  }, []);

  const handleTouchDragMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const td = touchDrag.current;
    if (!td || e.pointerId !== td.pointerId) return;
    const overId = rowIdFromPoint(e.clientX, e.clientY);
    setDragOverId(overId && overId !== td.sourceId ? overId : null);
  }, []);

  const handleTouchDragEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const td = touchDrag.current;
    if (!td || e.pointerId !== td.pointerId) return;
    touchDrag.current = null;
    setDraggedId(null);
    setDragOverId(null);
    if (e.type === 'pointercancel') return;
    const targetId = rowIdFromPoint(e.clientX, e.clientY);
    if (targetId) void commitReorder(td.sourceId, targetId);
  }, [commitReorder]);

  // Error state
  if (accountsError) {
    return (
      <div className="empty">
        <div className="empty-icon">!</div>
        <div className="empty-title">{t('accounts.load_failed')}</div>
        <div className="empty-text">{accountsError}</div>
        <button onClick={handleRetry} className="btn btn-primary mt-4">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  // Format the balance for hero display
  const balanceDisplay = formatLargeCurrency(summary.totalBalance);

  // Calculate monthly change
  const monthlyChange = summary.monthNet;
  const isPositiveChange = monthlyChange >= 0;

  return (
    <div className="accounts-page animate-fade">
      {/* Hero Section - Total Balance */}
      {loading ? (
        <HeroSkeleton />
      ) : (
        <div className="hero">
          <div className="hero-header">
            <div className="hero-label">{t('accounts.total_balance')}</div>
            <PrivacyToggle />
          </div>
          <div className="hero-amount">
            {isPrivacyMode ? (
              <span className="private-amount masked">••••</span>
            ) : (
              <>
                {balanceDisplay.main}
                <span style={{ fontSize: '0.5em', opacity: 0.6 }}>{balanceDisplay.cents}</span>
              </>
            )}
          </div>
          <div className={`hero-change ${!isPositiveChange ? 'negative' : ''}`}>
            <span>{isPositiveChange ? '↑' : '↓'}</span>
            <span>
              {isPrivacyMode
                ? t('accounts.this_month_change', { amount: '•••' })
                : t('accounts.this_month_change', {
                    amount: `${isPositiveChange ? '+' : ''}${formatCurrency(monthlyChange)}`,
                  })}
            </span>
          </div>
        </div>
      )}

      {/* Summary Bar - Income/Expenses */}
      {loading ? (
        <SummaryBarSkeleton />
      ) : (
        <div className="summary-bar">
          <div className="stat-card">
            <div className="stat-label">{t('common.income')}</div>
            <div className="stat-value amount--income">
              {isPrivacyMode ? (
                <span className="private-amount masked">+••••</span>
              ) : (
                <>+{formatCurrency(summary.monthIncome)}</>
              )}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">{t('common.expenses')}</div>
            <div className="stat-value amount--expense">
              {isPrivacyMode ? (
                <span className="private-amount masked">-••••</span>
              ) : (
                <>-{formatCurrency(summary.monthExpense)}</>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Section Header */}
      <div className="section-head">
        <span className="section-title">{t('accounts.all_accounts')}</span>
        <button
          className="section-action"
          onClick={() => setShowAddAccount(true)}
        >
          + {t('common.add')}
        </button>
      </div>

      {/* Account List */}
      <div className="list-card">
        {loading ? (
          // Show skeletons while loading
          Array.from({ length: 5 }).map((_, i) => (
            <AccountRowSkeleton key={i} />
          ))
        ) : accounts.length === 0 ? (
          <div className="empty">
            <div className="empty-icon-circle">
              <span>💳</span>
            </div>
            <div className="empty-title">{t('accounts.empty_title')}</div>
            <div className="empty-text">{t('accounts.empty_text')}</div>
            <div className="empty-actions">
              <Button
                variant="primary"
                onClick={() => navigate('/import')}
                className="import-btn-hero"
              >
                <span className="import-btn-icon">📥</span>
                {t('accounts.import_data')}
              </Button>
              <span className="empty-divider">{t('common.or')}</span>
              <Button
                variant="ghost"
                onClick={() => setShowAddAccount(true)}
                className="add-account-secondary"
              >
                {t('accounts.add_manually')}
              </Button>
            </div>
          </div>
        ) : (
          displayedAccounts.map((account, idx) => {
            const colors = getAccountColor(account.type);
            const icon = getAccountIcon(account.name, account.fullName);
            const isZeroBalance = account.balance === 0;
            const isDragOver = dragOverId === account.id;
            const isDragging = draggedId === account.id;
            const dragOverClass = isDragOver && draggedIdx !== -1
              ? (idx < draggedIdx ? 'drag-over-above' : 'drag-over-below')
              : '';

            return (
              <div
                key={account.id}
                data-account-id={account.id}
                className={`account-row ${dragOverClass} ${isDragging ? 'dragging' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, account.id!)}
                onDragEnd={handleDragEnd}
                onDragEnter={(e) => handleDragEnter(e, account.id!)}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, account.id!)}
              >
                <div
                  className="drag-handle"
                  title={t('accounts.drag_reorder')}
                  onPointerDown={(e) => handleTouchDragStart(e, account.id!)}
                  onPointerMove={handleTouchDragMove}
                  onPointerUp={handleTouchDragEnd}
                  onPointerCancel={handleTouchDragEnd}
                >
                  ⋮⋮
                </div>
                <div
                  className="account-icon"
                  style={{ background: colors.bg, color: colors.color }}
                >
                  {icon}
                </div>
                <div className="account-info">
                  <div className="account-name">{account.name}</div>
                  {account.fullName && (
                    <div className="account-fullname">{account.fullName}</div>
                  )}
                </div>
                <div className={`account-balance ${isZeroBalance ? 'text-muted' : ''}`}>
                  {isPrivacyMode ? (
                    <span className="private-amount masked">••••</span>
                  ) : (
                    formatCurrency(account.balance, account.currency)
                  )}
                </div>
                <Link
                  to={`/transactions?accountId=${account.id}`}
                  className="account-txn-link"
                  title={t('accounts.view_transactions')}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </Link>
                <button
                  className="account-menu-btn"
                  onClick={() => handleAccountClick(account)}
                  title={t('accounts.options_title')}
                >
                  ⋮
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Crypto / tracked holdings - auto-appears when detected (see
          CRYPTO_HOLDINGS_PLAN.md). Value is derived + adjustments, kept
          OUT of the total balance so bank total = spendable money. */}
      {!loading && visibleHoldings.length > 0 && (
        <>
          <div className="section-head">
            <span className="section-title">{t('crypto.section_title')}</span>
            <Link to="/crypto" className="section-action">
              {t('crypto.section_open')}
            </Link>
          </div>
          <div className="list-card">
            {visibleHoldings.map((h) => (
              <Link key={h.group.id} to="/crypto" className="account-row holdings-row">
                <div className="account-icon holdings-icon">
                  {h.group.icon || '🪙'}
                </div>
                <div className="account-info">
                  <div className="account-name">{h.group.name}</div>
                  <div className="account-fullname">{t('crypto.not_in_total')}</div>
                </div>
                <div className="account-balance holdings-balance">
                  {isPrivacyMode ? (
                    <span className="private-amount masked">••••</span>
                  ) : (
                    formatCurrency(h.trackedValue)
                  )}
                </div>
                <span className="holdings-chevron">›</span>
              </Link>
            ))}
          </div>
        </>
      )}

      {/* Zero-balance collapse - the old button here displayed a count but
          did nothing; now it actually expands/collapses those rows */}
      {!loading && collapseZero && (
        <div className="zero-accounts-wrap">
          <button
            className="zero-accounts-toggle"
            onClick={toggleZeroBalance}
            aria-expanded={showZeroBalance}
          >
            <svg
              width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
              className={showZeroBalance ? 'open' : ''}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            {t('accounts.zero_balance_count', { count: zeroAccounts.length })}
          </button>
        </div>
      )}

      {/* Hidden accounts - collapsed by default, unhide without leaving the
          page (Settings still has its Restore section too) */}
      {!loading && hiddenAccounts.length > 0 && (
        <div className="zero-accounts-wrap hidden-accounts-wrap">
          <button
            className="zero-accounts-toggle"
            onClick={() => setShowHidden((v) => !v)}
            aria-expanded={showHidden}
          >
            <svg
              width="12" height="12" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
              className={showHidden ? 'open' : ''}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            {t(
              hiddenAccounts.length === 1
                ? 'accounts.hidden_count_one'
                : 'accounts.hidden_count_other',
              { count: hiddenAccounts.length }
            )}
          </button>
          {showHidden && (
            <div className="list-card hidden-accounts-list">
              {hiddenAccounts.map((account) => {
                const colors = getAccountColor(account.type);
                const icon = getAccountIcon(account.name, account.fullName);
                return (
                  <div key={account.id} className="account-row hidden-account-row">
                    <div
                      className="account-icon"
                      style={{ background: colors.bg, color: colors.color }}
                    >
                      {icon}
                    </div>
                    <div className="account-info">
                      <div className="account-name">{account.name}</div>
                      {account.fullName && (
                        <div className="account-fullname">{account.fullName}</div>
                      )}
                    </div>
                    <div className="account-balance text-muted">
                      {isPrivacyMode ? (
                        <span className="private-amount masked">••••</span>
                      ) : (
                        formatCurrency(account.balance, account.currency)
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleUnhideAccount(account.id!)}
                      disabled={unhidingId === account.id}
                      className="unhide-btn"
                    >
                      {t('accounts.unhide')}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Account Actions Modal */}
      <AccountActionsModal
        account={selectedAccount}
        allAccounts={accounts}
        onClose={() => setSelectedAccount(null)}
        onDelete={handleDeleteAccount}
        onDeactivate={handleDeactivateAccount}
        onTransfer={handleTransfer}
        onRename={handleRenameAccount}
        onAdjustBalance={handleAdjustBalance}
        onRefresh={refresh}
      />

      {/* Add Account Modal */}
      <AddAccountModal
        isOpen={showAddAccount}
        onClose={() => setShowAddAccount(false)}
        onSuccess={refresh}
      />
    </div>
  );
}
